import React, { useState, useEffect, useRef, memo } from 'react';
import { withSnackbar } from 'notistack';
import { withStyles } from '@material-ui/core';
import {
  DeleteOutline as VoidPickupIcon,
  RestoreFromTrash as UnvoidPickupIcon,
} from '@material-ui/icons';
import styles from './styles';
import { compose } from 'recompose';
import { withAppConfig } from '../../../../../../../../appConfigContext';
import { PICKUP_MANAGEMENT_MODES } from '../../../PickupManagement/constants';
import StyledToolTip from '../../../../../../../common/StyledToolTip';

const RenderPickupIcon = ({onClickHandler, iconComponent, title}) => (
  <div onClick={onClickHandler}>
    <StyledToolTip title={title}>
      {iconComponent}
    </StyledToolTip>
  </div>
);

const isSameGroupSelection = (selectedPickups) => {
  if (selectedPickups.length <= 1) {
    return false;
  }
  const firstGroupId = selectedPickups[0]?.groupId;
  if (!firstGroupId) {
    return false;
  }
  return selectedPickups.every((pickup) => pickup.groupId === firstGroupId);
};

const VoidAndUnvoidPickup = ({ classes, eventHub, headerId, configJSON }) => {
  const [pickupFlowFlags, setPickupFlowFlags] = useState({
    isVoidPickupFlow: configJSON.enableVoidPickup,
    isUnvoidPickupFlow: configJSON.enableUnvoidPickup,
    isSingleRowSelected: false,
    isBulkVoidFlow: false
  });
  const isCompletedPickupRef = useRef(false);
  const isVoidedPickupRef = useRef(false);
  const hasInvalidBulkPickupRef = useRef(false);

  useEffect(() => {
    eventHub.on(`${headerId}_updatePickupManagementIconStatus`, changeVoidPickupStatus);
    return () => {
      eventHub.off(`${headerId}_updatePickupManagementIconStatus`, changeVoidPickupStatus);
    };
  }, []);

  const handleVoidPickup = () => {
    eventHub.emit('pickupManagement', {
      action: pickupFlowFlags.isBulkVoidFlow
        ? PICKUP_MANAGEMENT_MODES.BULK_VOID_PICKUP
        : PICKUP_MANAGEMENT_MODES.VOID_PICKUP
    });
  };

  const handleUnvoidPickup = () => {
    eventHub.emit('pickupManagement', {
      action: PICKUP_MANAGEMENT_MODES.UNVOID_PICKUP
    });
  };

  const changeVoidPickupStatus = ({ selectedPickups }) => {
    const { isCompleted, isVoided } = selectedPickups[0] ?? {};
    isCompletedPickupRef.current = isCompleted;
    isVoidedPickupRef.current = isVoided;
    const rowSelected = selectedPickups.length;
    const isBulkGroupSelected = configJSON.enableBulkEditAndVoid && isSameGroupSelection(selectedPickups);
    hasInvalidBulkPickupRef.current = isBulkGroupSelected
      ? selectedPickups.some(
          (pickup) => pickup.isCompleted || pickup.isVoided
        )
      : false;
    const isBulkVoidFlow =
      isBulkGroupSelected &&
      !hasInvalidBulkPickupRef.current &&
      Boolean(configJSON.enableVoidPickup);
    const isVoidPickupFlow = rowSelected === 1 && !isCompleted && !isVoided;
    const isUnvoidPickupFlow = rowSelected === 1 && !isCompleted && isVoided;
    setPickupFlowFlags({
      isVoidPickupFlow: isVoidPickupFlow || isBulkVoidFlow,
      isUnvoidPickupFlow,
      isSingleRowSelected: rowSelected === 1,
      isBulkVoidFlow
    });
  };

  const getVoidTooltipMessage = () => {
    if (!configJSON.enableVoidPickup) {
      return `Void Pickup (Disabled)`;
    }
    if (pickupFlowFlags.isBulkVoidFlow) {
      return 'Click to Void Group';
    }
    if (hasInvalidBulkPickupRef.current) {
      return 'Completed/Voided pickups cannot be voided';
    }
    if (!pickupFlowFlags.isSingleRowSelected) {
      return configJSON.enableBulkEditAndVoid
        ? 'Select a group or single pickup to void'
        : 'Select a single pickup to void';
    } else if (isVoidedPickupRef.current) {
      return 'Pickup is already voided';
    } else if (isCompletedPickupRef.current) {
      return 'Pickup is already completed';
    } else {
      return 'Click to Void Pickup';
    }
  };

  const getUnVoidTooltipMessage = () => {
    if (!configJSON.enableUnvoidPickup) {
      return `UnVoid Pickup (Disabled)`;
    } else if (!pickupFlowFlags.isSingleRowSelected) {
      return 'Select a single pickup to unvoid';
    } else if (isCompletedPickupRef.current) {
      return 'Pickup is already completed';
    } else if (!isVoidedPickupRef.current) {
      return 'Pickup is not voided yet';
    } else {
      return 'Click to Unvoid Pickup';
    }
  };

  const isEnabledIcon =
    (pickupFlowFlags.isSingleRowSelected &&
      ((configJSON.enableVoidPickup && pickupFlowFlags.isVoidPickupFlow) ||
        (configJSON.enableUnvoidPickup && pickupFlowFlags.isUnvoidPickupFlow))) ||
    pickupFlowFlags.isBulkVoidFlow;
  return (
    <div
      className={`${classes.headerIconStyles} ${classes.iconLayout} glHeaderIcons ${
        isEnabledIcon ? classes.enabledIcon : classes.disableIcon
      }`}>
      {pickupFlowFlags.isVoidPickupFlow ? (
        <RenderPickupIcon
          onClickHandler={isEnabledIcon ? handleVoidPickup : null}
          iconComponent={<VoidPickupIcon />}
          title={getVoidTooltipMessage()}
        />
      ) : (
        <RenderPickupIcon
          onClickHandler={isEnabledIcon ? handleUnvoidPickup : null}
          iconComponent={<UnvoidPickupIcon />}
          title={getUnVoidTooltipMessage()}
        />
      )}
    </div>
  );
};

export default compose(
  withStyles(styles),
  withAppConfig,
  withSnackbar
)(memo(VoidAndUnvoidPickup));

